import PageHero from "../components/PageHero";
import { Award, HeartHandshake, Leaf, Target } from "lucide-react";

const PILLARS = [
  { icon: Target, t: "Precision sourcing", d: "Every material is matched to your exact INCI, grade and documentation needs." },
  { icon: HeartHandshake, t: "Long-term partnerships", d: "We grow with our customers — from lab-scale to commercial volumes." },
  { icon: Leaf, t: "Mindful ingredients", d: "Preference for sustainably sourced, green-chemistry compliant raw materials." },
  { icon: Award, t: "Compliance-first", d: "FDA, ECOCERT, COSMOS, Halal, Kosher — we help you tick the right boxes." },
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About Goyam"
        title="Where ingredients meet intention."
        description="Every consignment tells a story of sourcing, stewardship, and trust. For nearly two decades, Goyam Ingredients has been the quiet thread connecting global principals and Indian formulators — delivering not just raw materials, but the certainty behind them."
        image="https://images.pexels.com/photos/8851404/pexels-photo-8851404.jpeg?auto=compress&cs=tinysrgb&w=2400"
      />

      <section className="section-y">
        <div className="container-x grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="eyebrow mb-4">Our story</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Built on relationships. Powered by detail.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-6 text-brand-ink/85 leading-relaxed">
            <p>
              Goyam Ingredients started as a specialized sourcing desk for a
              handful of regional beauty brands searching for reliable access to
              international cosmetic raw materials. Over the years, we've grown
              into a trusted import partner for manufacturers across the country
              — but our obsession with consistency has never changed.
            </p>
            <p>
              We believe raw materials aren't commodities. They are the
              architecture of every cream, serum, shampoo and fragrance a brand
              ships to its customers. That's why we spend time with principals,
              vet every certificate, test every shipment, and answer every
              technical question — no matter how small.
            </p>
            <p>
              Today, Goyam works with 120+ global principals across 40+
              countries, serving a wide spectrum of BPC applications with a
              catalogue of 500+ materials and growing.
            </p>
          </div>
        </div>
      </section>

      <section className="section-y bg-brand-surface/60 border-y border-brand-line/60">
        <div className="container-x">
          <div className="grid md:grid-cols-2 gap-14 items-center">
            <div>
              <p className="eyebrow mb-4">Mission & values</p>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-6">
                To be the most <span className="italic text-brand-terracotta">dependable</span> partner in BPC sourcing.
              </h2>
              <p className="text-brand-muted leading-relaxed">
                Dependability isn't glamorous. It's COAs that match the bottle,
                shipments that clear on schedule, and a human who picks up the
                phone when a scale-up is 48 hours away. It's what we live by.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {PILLARS.map((p) => (
                <div
                  key={p.t}
                  className="p-7 bg-brand-surface border border-brand-line hover:border-brand-moss transition"
                  data-testid={`pillar-${p.t.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <p.icon className="w-6 h-6 text-brand-terracotta mb-4" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl mb-2">{p.t}</h3>
                  <p className="text-sm text-brand-muted leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-y">
        <div className="container-x grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="eyebrow mb-4">Quality & compliance</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Documentation that formulators & regulators trust.
            </h2>
          </div>
          <div className="md:col-span-7">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-brand-line">
              {[
                "ISO 9001",
                "GMP",
                "ECOCERT",
                "COSMOS",
                "Halal",
                "Kosher",
                "USP/BP/EP",
                "FDA Notified",
                "REACH",
              ].map((c) => (
                <div
                  key={c}
                  className="bg-brand-surface p-6 flex items-center justify-center text-center text-sm tracking-wide font-medium text-brand-ink/85"
                >
                  {c}
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-brand-muted">
              Certifications vary by material. Ask for specific COA, MSDS,
              IFRA, and regulatory filings with your quote.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
